import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.LaborCost.spreadsheet';

/**
 * 生産工場費用設定 SpreadSheet同期
 *
 * シミュレーション画面「生産工場費用設定」の以下の項目を GoogleSpread 上で編集可能にする。
 *   - 生産工場労務費単価（スカラー）
 *   - 生産工場経費単価（スカラー）
 *   - 労務費ベースアップ率（スカラー、%）
 *   - 生産工場労務費（月別）
 *   - 償却費以外の工場経費（月別）
 *
 * 他項目（販売数量・単価など）と同じく「画面→Spread」「Spread→画面」の
 * 双方向変換関数を提供する。
 */

/** シート名（他項目のSHEET_NAME定数と同様に英語で定義） */
export const SHEET_NAME_LABOR_COST = "LaborCost";

/**
 * 項目名（Spread連携時のキーとして使用するため日本語固定）
 * 読み戻し時はこのラベルで行を判定する。
 */
export const LABOR_COST_ITEM = {
  laborUnitPrice: i18n.t(`${T_PREFIX}.header.productionFactoryLaborUnitPrice`),
  expenseUnitPrice: i18n.t(`${T_PREFIX}.header.productionFactoryExpenseUnitPrice`),
  baseUpRate: i18n.t(`${T_PREFIX}.header.baseLaborCostIncreaseRate`),
  laborCost: i18n.t(`${T_PREFIX}.header.productionLaborCost`),
  overseasExpenses: i18n.t(`${T_PREFIX}.header.overHeadCost`),
} as const;

/**
 * 生産工場費用設定データ変換結果（Spread→画面）
 */
export interface LaborCostConversionResult {
  /** 生産工場労務費（月別） */
  productionLaborCostByMonth: number[];
  /** 償却費以外の工場経費（月別） */
  overseasFactoryExpensesByMonth: number[];
  /** 生産工場労務費単価 */
  productionFactoryLaborUnitPrice: number | null;
  /** 生産工場経費単価 */
  productionFactoryExpenseUnitPrice: number | null;
  /** 労務費ベースアップ率 */
  baseLaborCostIncreaseRate: number | null;
  /** 無効値エラーメッセージ */
  errors: string[];
}

/** 文字列セルを数値へ変換（カンマ除去。空欄・非数値は null） */
const parseCell = (raw: unknown): number | null => {
  if (raw === "" || raw === null || raw === undefined) return null;
  const cleaned = String(raw).replace(/,/g, "").trim();
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? NaN : n;
};

/**
 * 画面データを SpreadSheet 書き込み用 2D 配列に変換する。
 *
 * レイアウト（単価・率は月別ではないため、月別テーブルとは別ブロックに分離）:
 *   ── 単価・率ブロック（2列） ──
 *   生産工場労務費単価      8
 *   生産工場経費単価        12
 *   労務費ベースアップ率     0
 *   （空行）
 *   ── 月別テーブル ──
 *   項目                  YYYY/MM  YYYY/MM ...
 *   生産工場労務費          100      200 ...
 *   償却費以外の工場経費     50       60 ...
 *
 * @param labels 年月ラベル配列（"YYYY/MM"）
 * @param laborCostByMonth 生産工場労務費（月別）
 * @param overseasFactoryExpensesByMonth 償却費以外の工場経費（月別）
 * @param productionFactoryLaborUnitPrice 生産工場労務費単価
 * @param productionFactoryExpenseUnitPrice 生産工場経費単価
 * @param baseLaborCostIncreaseRate 労務費ベースアップ率
 * @returns 2D 配列
 */
export const laborCostToSpreadSheetData = (
  labels: string[],
  laborCostByMonth: number[],
  overseasFactoryExpensesByMonth: number[],
  productionFactoryLaborUnitPrice: number | null,
  productionFactoryExpenseUnitPrice: number | null,
  baseLaborCostIncreaseRate: number | null,
): (string | number)[][] => {
  const monthCount = labels.length;

  // 月別行
  const monthlyRow = (label: string, values: number[]): (string | number)[] => {
    const row: (string | number)[] = [label];
    for (let i = 0; i < monthCount; i++) row.push(values[i] ?? 0);
    return row;
  };

  const rows: (string | number)[][] = [
    // 単価・率ブロック（月別ではないので独立した2列の行）
    [LABOR_COST_ITEM.laborUnitPrice, productionFactoryLaborUnitPrice ?? 0],
    [LABOR_COST_ITEM.expenseUnitPrice, productionFactoryExpenseUnitPrice ?? 0],
    [LABOR_COST_ITEM.baseUpRate, baseLaborCostIncreaseRate ?? 0],
    // 空行でブロックを区切る
    [],
    // 月別テーブル（ヘッダー + 各行）
    [i18n.t(`${T_PREFIX}.header.item`), ...labels],
    monthlyRow(LABOR_COST_ITEM.laborCost, laborCostByMonth),
    monthlyRow(LABOR_COST_ITEM.overseasExpenses, overseasFactoryExpensesByMonth),
  ];

  // 全行の列数を最大列数に統一（SpreadSheet APIのrangeが先頭行の列数で決まるため）
  const maxCols = Math.max(...rows.map(r => r.length), 0);
  return rows.map(r =>
    r.length < maxCols ? [...r, ...Array(maxCols - r.length).fill("")] : r
  );
};

/**
 * SpreadSheet データを画面データに変換する。
 *
 * @param values SpreadSheet から取得した 2D 配列（ヘッダー行含む）
 * @param monthCount 年月数（月別配列の長さ）
 * @returns 変換結果
 */
export const spreadSheetDataToLaborCost = (
  values: unknown[][],
  monthCount: number,
): LaborCostConversionResult => {
  const errors: string[] = [];

  const result: LaborCostConversionResult = {
    productionLaborCostByMonth: Array(monthCount).fill(0),
    overseasFactoryExpensesByMonth: Array(monthCount).fill(0),
    productionFactoryLaborUnitPrice: null,
    productionFactoryExpenseUnitPrice: null,
    baseLaborCostIncreaseRate: null,
    errors,
  };

  if (!values || values.length === 0) {
    return result;
  }

  // スカラー値を読み取る（B列 = index 1）
  const readScalar = (row: unknown[], label: string): number | null => {
    const parsed = parseCell(row[1]);
    if (parsed !== null && Number.isNaN(parsed)) {
      errors.push(i18n.t(`${T_PREFIX}.message.invalid`, { label: label, value: row[1] }));
      return 0;
    }
    return parsed;
  };

  // 月別値を読み取る（C列以降を monthCount 分）
  const readMonthly = (row: unknown[], label: string): number[] => {
    const arr: number[] = [];
    for (let i = 0; i < monthCount; i++) {
      const parsed = parseCell(row[i + 1]);
      if (parsed !== null && Number.isNaN(parsed)) {
        const ymSuffix = ` (${i + 1}列目)`;
        errors.push(i18n.t(`${T_PREFIX}.message.invalid`, { label: label + ymSuffix, value: row[i + 1] }));
        arr.push(0);
      } else {
        arr.push(parsed ?? 0);
      }
    }
    return arr;
  };

  for (const row of values) {
    if (!row || row.length === 0) continue;
    const label = String(row[0] ?? "").trim();

    switch (label) {
      case LABOR_COST_ITEM.laborUnitPrice:
        result.productionFactoryLaborUnitPrice = readScalar(row, label);
        break;
      case LABOR_COST_ITEM.expenseUnitPrice:
        result.productionFactoryExpenseUnitPrice = readScalar(row, label);
        break;
      case LABOR_COST_ITEM.baseUpRate:
        result.baseLaborCostIncreaseRate = readScalar(row, label);
        break;
      case LABOR_COST_ITEM.laborCost:
        result.productionLaborCostByMonth = readMonthly(row, label);
        break;
      case LABOR_COST_ITEM.overseasExpenses:
        result.overseasFactoryExpensesByMonth = readMonthly(row, label);
        break;
      default:
        break;
    }
  }

  return result;
};

/**
 * 生産工場費用設定シートの体裁を整える（A列＝項目列を広げる）。
 * 他シートの setup 関数と同じく GoogleAPI インスタンスを受け取る。
 *
 * @param googleAPI GoogleAPI インスタンス
 * @param spreadsheetId スプレッドシートID
 * @param sheetId シートID
 */
export const setupLaborCostSheet = async (
  googleAPI: { setColumnWidth: (spreadsheetId: string, sheetId: number, startIndex: number, endIndex: number, width: number) => Promise<unknown> },
  spreadsheetId: string,
  sheetId: number,
): Promise<void> => {
  // A列（項目列）の幅を広げる
  await googleAPI.setColumnWidth(spreadsheetId, sheetId, 0, 1, 220);
};
